import { c as _c } from "react/compiler-runtime";
// Task / entity detail in the POC's markup: head (mono id, h1), status buttons, fields, context (tasks), body, events. The desktop
// page and the mobile sheet render the same parts; the sheet adds the grip, the cover bleed and the detent markers.
// The fields are the kind's view when it has one (src/views, hq Decision web-kind-views), else the generic ones.
import { type RefObject, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type Detail, local, type Task } from "@/api";
import { useBlobUrl } from "@/lib/blob";
import { errorText, useData } from "@/store";
import { nameOf, useView, useViews } from "@/views";
import { ContextSection } from "./context";
import { Fields, Ref, time } from "./fields";
import { Markdown } from "./markdown";
import { PageHead } from "./page";
import { ErrorLine } from "./error-line";
import { Segment, Segmented } from "./segmented";
import { SectionHead } from "./section-head";
import { ThreadView } from "./thread";
const show = (v: unknown) => v === null || v === undefined ? "∅" : typeof v === "string" ? v : JSON.stringify(v);

/** Fetches the detail on selection, again on every snapshot refresh and on reload() (after a context change). */
export function useDetail(id) {
  const $ = _c(11);
  const snapshot = useData(_temp);
  const [rev, setRev] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      detail: null,
      error: null
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [state, setState] = useState(t0);
  let t1;
  if ($[1] !== id) {
    t1 = () => {
      let live = true;
      api.entities.get(id).then(detail => live && setState({
        detail,
        error: null
      }), e => live && setState(s_0 => ({
        ...s_0,
        error: errorText(e)
      })));
      return () => {
        live = false;
      };
    };
    $[1] = id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== id || $[4] !== rev || $[5] !== snapshot) {
    t2 = [id, snapshot, rev];
    $[3] = id;
    $[4] = rev;
    $[5] = snapshot;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useEffect(t1, t2);
  const t3 = state.detail?.id === id ? state.detail : null;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setRev(_temp2);
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== state.error || $[9] !== t3) {
    t5 = {
      detail: t3,
      error: state.error,
      reload: t4
    };
    $[8] = state.error;
    $[9] = t3;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}

/** Status change by button (desktop page and the sheet's peek detent). */
function _temp2(n) {
  return n + 1;
}
function _temp(s) {
  return s.snapshot;
}
export function StatusButtons(t0) {
  const $ = _c(27);
  const {
    id
  } = t0;
  const snapshot = useData(_temp3);
  const move = useData(_temp4);
  let t1;
  if ($[0] !== id || $[1] !== snapshot?.tasks) {
    t1 = snapshot?.tasks.find(x => x.id === id);
    $[0] = id;
    $[1] = snapshot?.tasks;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const task = t1;
  let T0;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[3] !== id || $[4] !== move || $[5] !== snapshot?.projects || $[6] !== snapshot?.statuses || $[7] !== task) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const names = snapshot?.projects.find(p => p.id === task?.projectId)?.statuses;
      if (!task || !names) {
        t6 = null;
        break bb0;
      }
      let t7;
      if ($[14] !== snapshot?.statuses) {
        t7 = n => snapshot?.statuses.find(x_0 => x_0.name === n)?.color ?? undefined;
        $[14] = snapshot?.statuses;
        $[15] = t7;
      } else {
        t7 = $[15];
      }
      const color = t7;
      T0 = Segmented;
      t2 = "pills";
      t3 = "status";
      t4 = "Status";
      let t8;
      if ($[16] !== color || $[17] !== id || $[18] !== move || $[19] !== task) {
        t8 = s_1 => <Segment key={s_1} data-t="status-button" data-status={s_1} color={color(s_1)} pressed={task.status === s_1} onClick={() => void move(id, s_1)}>{s_1}</Segment>;
        $[16] = color;
        $[17] = id;
        $[18] = move;
        $[19] = task;
        $[20] = t8;
      } else {
        t8 = $[20];
      }
      t5 = names.map(t8);
    }
    $[3] = id;
    $[4] = move;
    $[5] = snapshot?.projects;
    $[6] = snapshot?.statuses;
    $[7] = task;
    $[8] = T0;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    T0 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[21] !== T0 || $[22] !== t2 || $[23] !== t3 || $[24] !== t4 || $[25] !== t5) {
    t7 = <T0 variant={t2} t={t3} label={t4}>{t5}</T0>;
    $[21] = T0;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
}

/** What the views add to a record's head (KindView.Head): where it was read from, what it belongs to. */
function _temp4(s_0) {
  return s_0.move;
}
function _temp3(s) {
  return s.snapshot;
}
export function Heads(t0) {
  const $ = _c(4);
  const {
    detail,
    onOpen
  } = t0;
  const views = useViews();
  let t1;
  if ($[0] !== detail || $[1] !== onOpen || $[2] !== views) {
    t1 = detail && <span className="heads" data-t="heads">{views.map((v, i) => v.Head && <v.Head key={i} detail={detail} onOpen={onOpen} />)}</span>;
    $[0] = detail;
    $[1] = onOpen;
    $[2] = views;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
type Props = {
  id: string;
  onOpen: (id: string) => void;
  onClose?: () => void;
};

/**
 * One detail. `sheet`: grip, cover as a full-bleed image above the head, a `.d.peek` marker after the status
 * buttons (the peek detent snaps the panel's bottom edge there) and `peekRef` on it.
 */
type BodyProps = Props & {
  sheet?: boolean;
  peekRef?: RefObject<HTMLDivElement | null>;
};
export function DetailBody(props) {
  const $ = _c(9);
  const {
    detail,
    error,
    reload
  } = useDetail(props.id);
  let t0;
  if ($[0] !== props.id) {
    t0 = s => s.snapshot?.tasks.find(x => x.id === props.id);
    $[0] = props.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const task = useData(t0);
  const cover = useBlobUrl(task?.cover);
  let t1;
  if ($[2] !== cover || $[3] !== detail || $[4] !== error || $[5] !== props || $[6] !== reload || $[7] !== task) {
    t1 = <DetailContent {...props} detail={detail} error={error} task={task} cover={cover} onChanged={reload} />;
    $[2] = cover;
    $[3] = detail;
    $[4] = error;
    $[5] = props;
    $[6] = reload;
    $[7] = task;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}

/** The markup alone (render tests). */
export function DetailContent({
  id,
  onOpen,
  onClose,
  sheet,
  peekRef,
  detail,
  error,
  task,
  cover,
  onChanged = () => {}
}: BodyProps & {
  detail: Detail | null;
  error: string | null;
  task: Task | undefined;
  cover: string | null;
  onChanged?: () => void;
}) {
  const {
    t
  } = useTranslation();
  const view = useView(detail?.fullKind);
  const title = task?.title ?? (detail ? nameOf(detail, view) : "…");
  const where = detail?.instance ?? task?.instance;
  const meta = [detail && detail.kind !== "Task" ? detail.kind : null, local(id), where ? `@${where}` : null].filter(Boolean).join(" · ");
  const head = <PageHead grip={sheet && !task?.cover} meta={meta} title={title} onClose={onClose} />;
  const links = detail?.links.filter(l => l.kind !== "blob") ?? [];
  /** Tasks show their links in the Context section, with the records they are about. */
  const isTask = detail?.kind === "Task";
  return <>
			{sheet && task?.cover ? <>
					<div className="bleed" data-t="cover">
						<img src={cover ?? undefined} decoding="async" alt="" />
						<div className="grip" />
					</div>
					{head}
				</> : <>
					{head}
					{task?.cover && <img className="hero" src={cover ?? undefined} decoding="async" alt="" />}
				</>}
			<Heads detail={detail} onOpen={onOpen} />
			{task && <StatusButtons id={id} />}
			{sheet && <div ref={peekRef} className="d peek" />}
			{error && <ErrorLine>{error}</ErrorLine>}
			{detail && <>
					{view === undefined ? null : view?.Body ? <view.Body detail={detail} onOpen={onOpen} /> : <Fields detail={detail} onOpen={onOpen} links={!isTask} />}
					{isTask && <ContextSection id={id} items={detail.context} links={links} onOpen={onOpen} onChanged={onChanged} />}
					{detail.doc ? <Markdown doc={detail.doc} onRef={onOpen} /> : detail.body && <div className="body">{detail.body}</div>}
					{isTask && <ThreadView about={id} space={task?.projectId ?? null} title={title} />}
					{detail.backlinks.length > 0 && <section data-t="backlinks">
							<SectionHead>{t("panel.backlinks")}</SectionHead>
							<dl className="fields">
								{detail.backlinks.map((f, i) => <div key={`${f.key}${i}`} className="contents">
										<dt>{f.key}</dt>
										<dd>{f.ref ? <Ref id={f.ref} label={f.value} onOpen={onOpen} /> : f.value}</dd>
									</div>)}
							</dl>
						</section>}
					{detail.events.length > 0 && <>
							<h2>{t("panel.events")}</h2>
							{detail.events.map((e, i_0) => <div key={`${e.at}${i_0}`} className="ev mono">
									{time(e.at).slice(5)} · {e.by} · {e.field} {show(e.from)} → {show(e.to)}
									{e.note && <div>{e.note}</div>}
								</div>)}
						</>}
				</>}
		</>;
}